import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { StaffTicketDetail } from "@signalist/shared-types";

import { classNames } from "@/utils/classNames";

import { TICKET_DATE_FORMAT } from "@/pages/Private/pages/Support/pages/TicketDetail/components/TicketMessage";
import { ticketLinkedEntityKey } from "@/pages/Private/pages/Support/utils/ticketRowMeta";

import { durationParts } from "../../../utils/staffTicketFormat";
import { firstReplyMinutes, waitingSince } from "../utils/ticketContext";
import { RailHeading, RailRow } from "./RailSection";

/** The mockup's "Context" grid (decision 6 for first reply and waiting since). */
export const TicketContextSection: FunctionComponent<{ ticket: StaffTicketDetail }> = ({
	ticket,
}) => {
	const { t } = useTranslation();
	const none = t("support.staff.detail.rail.none");
	const linked = ticketLinkedEntityKey(ticket);
	const firstReply = firstReplyMinutes(ticket);
	const waiting = waitingSince(ticket);
	const page = [ticket.pageLabel, ticket.routePattern].filter(Boolean).join(" · ");

	return (
		<>
			<RailHeading>{t("support.staff.detail.rail.context")}</RailHeading>
			<div className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
				<RailRow label={t("support.staff.detail.rail.relatedTo")}>
					{linked ? t(`support.linkedEntity.${linked.key}`, { id: linked.id }) : none}
				</RailRow>
				<RailRow label={t("support.staff.detail.rail.page")}>{page || none}</RailRow>
				<RailRow label={t("support.staff.detail.rail.source")}>
					{t(`support.detail.source.${ticket.source}`)}
				</RailRow>
				<RailRow label={t("support.staff.detail.rail.opened")}>
					{format(new Date(ticket.createdAt), TICKET_DATE_FORMAT)}
				</RailRow>
				<RailRow label={t("support.staff.detail.rail.firstReply")}>
					{firstReply === null
						? none
						: t(`support.staff.units.${durationParts(firstReply).unit}`, {
								value: durationParts(firstReply).value,
							})}
				</RailRow>
				{waiting && (
					<RailRow label={t("support.staff.detail.rail.waitingSince")}>
						<span className={classNames(waiting.onUs ? "font-medium text-status-error" : "")}>
							{format(new Date(waiting.at), TICKET_DATE_FORMAT)}
						</span>
					</RailRow>
				)}
			</div>
		</>
	);
};
